import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import BaseTable, { Column } from 'react-base-table';
import { TableWrapper } from './styled';
import { SortableRow } from './SortableRow';
import { TableHeader } from './TableHeader';
import useEventsOverview from '../../hooks/useEventsOverview';
import Retry from '@src/components/common/Retry';
import { EVENT_METRICS } from '@src/hooks/event/useEventConfigs';
import { getMetricDisplayName } from '@src/utils/metrics/metricHelpers';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import useDndUtils from '../hooks/useDndUtils';

const dragHandleWidth = 32;

// Extract row renderer as a separate component to avoid hooks-in-callback linting error
const RowRenderer = ({ rowData, nameColumnWidth, columnWidth, onDataUpdate }) => (
  <SortableRow
    signalMeta={rowData}
    nameColumnWidth={nameColumnWidth}
    columnWidth={columnWidth}
    onDataUpdate={onDataUpdate}
  />
);

export default function EventsTable() {
  const { events, validEvents, isLoading, isError, refetch } = useEventsOverview();
  const [width, setWidth] = useState(800);
  const [eventDataMap, setEventDataMap] = useState<Record<string, number>>({});
  const containerRef = useRef(null);
  const { handleDragEnd } = useDndUtils(events);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  );

  const rows = useMemo(() => {
    // Only show events that are persisted by the user
    if (!events || events.length === 0) {
      return [];
    }

    // Filter validEvents to only include persisted events
    const selectedEvents = validEvents.filter((event) => events.includes(event.event_name));
    const selectedEventNames = new Set(selectedEvents.map((event) => event.event_name));

    // Map to row format
    const eventRows = selectedEvents.map((event) => {
      const eventName = event.event_name;
      const totalEvents = event.AppTotalEvents || 0;
      return {
        id: eventName,
        name: eventName,
        totalEvents,
        sortValue: eventDataMap[eventName] || totalEvents,
      };
    });

    // Keep missing persisted events visible so users can remove stale/deleted entries
    const deletedEventRows = events
      .filter((eventName) => !selectedEventNames.has(eventName))
      .map((eventName) => ({
        id: eventName,
        name: eventName,
        totalEvents: 0,
        sortValue: 0,
        isDeleted: true,
      }));

    // Sort by the order in events array to maintain user's persisted drag order
    return [...eventRows, ...deletedEventRows].sort((a, b) => {
      const indexA = events.indexOf(a.id);
      const indexB = events.indexOf(b.id);
      return indexA - indexB;
    });
  }, [validEvents, events, eventDataMap]);

  // Calculate equal column widths; effectiveNameWidth narrows the name cell for the drag handle
  const columnCount = 4; // name, count, impactedSessions, impactedUsers
  const columnWidth = useMemo(() => width / columnCount, [width, columnCount]);
  const effectiveNameWidth = useMemo(() => columnWidth - dragHandleWidth, [columnWidth]);

  const rowHeight = 72;
  const headerHeight = 38;
  const height = rows.length * rowHeight + headerHeight;

  // Update width based on container size
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const containerWidth = entries[0].contentRect.width;
        setWidth(containerWidth);
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const rowRenderer = useCallback(
    ({ rowData }) => (
      <RowRenderer
        rowData={rowData}
        nameColumnWidth={effectiveNameWidth}
        columnWidth={columnWidth}
        onDataUpdate={setEventDataMap}
      />
    ),
    [effectiveNameWidth, columnWidth, setEventDataMap],
  );

  // Show empty state if no events are selected
  if (events.length === 0) {
    return (
      <TableWrapper ref={containerRef} className="events-table-container">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            color: '#999',
            fontSize: '14px',
          }}
        >
          No events selected. Click Add Event to select events to display.
        </div>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper ref={containerRef} className="events-table-container">
      <Retry
        progress
        isLoading={isLoading}
        showError={isError}
        fetchData={refetch}
        queued={false}
        horizontal={false}
        compact={false}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={events} strategy={verticalListSortingStrategy}>
            <BaseTable
              data={rows}
              rowKey="id"
              width={width}
              height={height}
              rowHeight={rowHeight}
              headerHeight={headerHeight}
              rowClassName={() => 'sortable-row'}
              rowRenderer={rowRenderer}
              overscanColumnCount={1}
            >
              <Column
                key="name"
                dataKey="name"
                width={columnWidth}
                align="left"
                frozen="left"
                headerRenderer={() => (
                  <TableHeader title="Event Name" paddingLeft={`${dragHandleWidth}px`} />
                )}
              />
              <Column
                key="count"
                dataKey="count"
                width={columnWidth}
                align="left"
                headerRenderer={() => (
                  <TableHeader
                    title={getMetricDisplayName({
                      metricKey: EVENT_METRICS.count,
                      defaultValue: 'Event Count',
                    })}
                  />
                )}
              />
              <Column
                key="impactedSessions"
                dataKey="impactedSessions"
                width={columnWidth}
                align="left"
                headerRenderer={() => (
                  <TableHeader
                    title={getMetricDisplayName({
                      metricKey: EVENT_METRICS.with_session,
                      defaultValue: 'Sessions Count',
                    })}
                  />
                )}
              />
              <Column
                key="impactedUsers"
                dataKey="impactedUsers"
                width={columnWidth}
                align="left"
                headerRenderer={() => (
                  <TableHeader
                    title={getMetricDisplayName({
                      metricKey: EVENT_METRICS.with_user,
                      defaultValue: 'Users Count',
                    })}
                  />
                )}
              />
            </BaseTable>
          </SortableContext>
        </DndContext>
      </Retry>
    </TableWrapper>
  );
}
